// import DatePicker from "react-datepicker";
// import "react-datepicker/dist/react-datepicker.css";
import { useState, useRef, useEffect } from 'react';
import {
  HStack,
  IconButton,
  Popover, PopoverTrigger, Portal, PopoverContent, PopoverArrow, PopoverBody,
  Text,
  VStack,
  Select,
} from "@chakra-ui/react";
import { TimeIcon } from '@chakra-ui/icons'

interface Props {
  /** 値(hh:mm) */
  value: string;
  /** チェンジイベント */
  onChange: (value: string) => void;
  /** フォーカスアウトイベント */
  onBlur?: () => void;
  /** エラー有無 */
  isInvalid: boolean;
}

/**
 * タイムピッカー
 * @param props パラメータ
 * @returns 
 */
const CeasTimePicker: React.FC<Props> = (props: Props) => {
  /** Hour部 */
  const hourRef = useRef<HTMLDivElement>(null);
  /** Minutes部 */
  const minuteRef = useRef<HTMLDivElement>(null);
  /** 時計アイコン */
  const timeRef = useRef<HTMLButtonElement>(null);
  /** Hourセレクト */
  const hourSelectRef = useRef<HTMLSelectElement>(null);
  /** Minutesセレクト */
  const minuteSelectRef = useRef<HTMLSelectElement>(null);

  /** Hour選択項目 */
  const [hourList, setHourList] = useState<number[]>(Array.from({ length: 24 }, (_, i) => i));
  /** Minutes選択項目 */
  const [minutesList, setMinutesList] = useState<number[]>(Array.from({ length: 60 }, (_, i) => i));

  /** コンポーネントフォーカス有無（フォーカス枠） */
  const [isFocused, setIsFocused] = useState<boolean>(false);
  /** Hour部 */
  const [hh, setHh] = useState<number>(0);
  /** Minutes部 */
  const [mm, setMm] = useState<number>(0);

  /** 無限ループ防止（回） */
  let times: number = 0;
  /** 無限ループ防止（カウント） */
  let count: number = 0;

  /**
   * 値変更時にhh,mmに反映
   */
  useEffect(() => {
    valueToHhmm();
  }, [props.value]);

  /**
   * 時分変更時にonChange発火
   */
  useEffect(() => {
    if (count < times) {
      count++;
      return;
    }

    count = 0;
    times = 0;

    if (props.onChange) {
      const hhmm = `${convDisplay(hh)}:${convDisplay(mm)}`;

      if (hhmm === props.value) {
        return;
      }

      props.onChange(hhmm);
    }
  }, [hh, mm]);

  /**
   * キーダウン時にフォーカスや値の増減を行う。
   * @param event イベント情報
   */
  const handleKeyDown = (event: React.KeyboardEvent) => {
    switch (event.key) {
      case 'Backspace':
        if (event.currentTarget === hourRef.current) {
          let tempHh = hh;
          if (tempHh < 10) {
            tempHh = 0;
          } else {
            tempHh = Math.floor(tempHh / 10);
          }

          setHh(tempHh);
        } else if (event.currentTarget === minuteRef.current) {
          let tempMm = mm;
          if (tempMm < 10) {
            tempMm = 0;
          } else {
            tempMm = Math.floor(tempMm / 10);
          }
          setMm(tempMm);
        }
        break;
      case 'Delete':
        if (event.currentTarget === hourRef.current) {
          setHh(0);
        } else if (event.currentTarget === minuteRef.current) {
          setMm(0);
        }
        break;
      case 'ArrowLeft':
        if (event.currentTarget === minuteRef.current) {
          hourRef.current?.focus();
        }
        break;
      case 'ArrowRight':
        if (event.currentTarget === hourRef.current) {
          minuteRef.current?.focus();
        }
        break;
      case 'ArrowUp':
        if (event.currentTarget === hourRef.current) {
          let hour = hh + 1;
          if (hour > 24) {
            hour = 0;
          }
          setMm(0);
          setHh(hour);
        } else if (event.currentTarget === minuteRef.current) {
          if (hh === 24) {
            setMm(0);
            break;
          }
          let minutes = mm + 1;
          if (minutes >= 60) {
            minutes = 0;
          }
          setMm(minutes);
        }
        break;
      case 'ArrowDown':
        if (event.currentTarget === hourRef.current) {
          let hour = hh - 1;
          if (hour < 0) {
            hour = 24;
            setMm(0);
          }
          setHh(hour);
        } else if (event.currentTarget === minuteRef.current) {
          if (hh === 24) {
            setMm(0);
            break;
          }
          let minutes = mm - 1;
          if (minutes < 0) {
            minutes = 59;
          }
          setMm(minutes);
        }
        break;
      case '0':
      case '1':
      case '2':
      case '3':
      case '4':
      case '5':
      case '6':
      case '7':
      case '8':
      case '9':
        if (event.currentTarget === hourRef.current) {
          // 既存入力と結合して数値化
          let hour = parseInt(hh.toString() + event.key);
          if (hour > 24) {
            // 24時間超過なら、入力値のみにする
            hour = parseInt(event.key);
          }

          if (hour === 24) {
            // 24:00まで
            setMm(0);
          }

          if (hour > 2) {
            // 時間が2を超過なら、Hourを2桁することはないので分へ移動
            minuteRef.current?.focus();
          }

          setHh(hour);
        } else if (event.currentTarget === minuteRef.current) {
          if (hh === 24) {
            setMm(0);
            break;
          }

          // 既存入力と結合して数値化
          let minutes = parseInt(mm.toString() + event.key);
          if (minutes >= 60) {
            // 60分以上なら、入力値のみにする
            minutes = parseInt(event.key);
          }
          setMm(minutes);
        }
        break;
    }
  }

  /**
   * フォーカス時にテキスト選択、外枠線を表示
   * @param event イベント情報
   */
  const handleTimeFocus = (event: React.FocusEvent<HTMLDivElement>) => {
    // 明示的にテキストを選択する
    const selection = window.getSelection();
    const range = document.createRange();
    range.selectNodeContents(event.currentTarget);
    selection?.removeAllRanges();
    selection?.addRange(range);
    setIsFocused(true);
  };

  /**
   * フォーカス時にテキスト選択、外枠線を表示
   */
  const handleFocus = () => {
    // 明示的にテキストを選択する
    setIsFocused(true);
  };

  /**
   * フォーカスアウト時に外枠線を非表示
   */
  const handleBlur = () => {
    // 少し待ってからフォーカスが完全に外れたことを確認
    requestAnimationFrame(() => {
      const active = document.activeElement;
      if (
        active !== hourRef.current
        && active !== minuteRef.current
        && active !== timeRef.current
        && active !== hourSelectRef.current
        && active !== minuteSelectRef.current

      ) {
        setIsFocused(false);
        valueToHhmm();
        if (props.onBlur) {
          props.onBlur();
        }
      }
    });
  };

  /**
   * hh:mm形式を時と分に反映
   */
  const valueToHhmm = () => {
    try {
      let [hour, minutes] = props.value.split(":").map((v) => {
        const result = parseInt(v);
        return result;
      });

      if (isNaN(hour)) {
        hour = 0;
      }
      if (isNaN(minutes)) {
        minutes = 0;
      }

      if (hour !== hh) {
        times++;
        setHh(hour);
      }

      if (minutes !== mm) {
        times++;
        setMm(minutes);
      }
    } finally {

    }
  }

  /**
   * 指定した数値をゼロ埋め
   * @param value 数値
   * @returns 
   */
  const convDisplay = (value: number): string => {
    return value.toString().padStart(2, '0');
  }


  /**
   * レイアウト
   */
  return (
    <HStack
      border="2px solid"
      borderColor={isFocused ? 'blue.400' : props.isInvalid ? 'red' : '#E2E8F0'}
      borderRadius="md"
      style={{ width: "110px", height: "40px", padding: "16px" }}
      gap={0}
    >
      {/* 時 */}
      <Text
        as="span"
        tabIndex={0}
        ref={hourRef}
        userSelect="all"
        onKeyDown={handleKeyDown}
        onFocus={handleTimeFocus}
        onBlur={handleBlur}
        _focus={{ boxShadow: 'none', outline: 'none' }}
      >{convDisplay(hh)}</Text>
      <Text>:</Text>
      {/* 分 */}
      <Text
        as="span"
        tabIndex={0}
        ref={minuteRef}
        userSelect="all"
        onKeyDown={handleKeyDown}
        onFocus={handleTimeFocus}
        onBlur={handleBlur}
        _focus={{ boxShadow: 'none', outline: 'none' }}
      >{convDisplay(mm)}</Text>

      <Popover>
        <PopoverTrigger>
          <IconButton
            aria-label='時計アイコン'
            variant="ghost"      // 透過スタイル
            icon={<TimeIcon />}
            _hover={{ background: 'none' }}
            _focus={{ boxShadow: 'none', outline: 'none' }}
            ref={timeRef}
            tabIndex={-1}
            onFocus={handleFocus}
            onBlur={handleBlur}
          />
        </PopoverTrigger>
        <Portal>
          <PopoverContent maxW="fit-content">
            <PopoverArrow />
            <PopoverBody>
              <HStack>
                <select
                  size={100}
                  style={{ width: "50px", height: "420px", border: "1px solid #E2E8F0", outline: "none", }}
                  ref={hourSelectRef}
                  value={hh}
                  onChange={(e) => {
                    const value = parseInt(e.target.value);
                    setHh(value);
                  }}
                  onFocus={handleFocus}
                  onBlur={handleBlur}
                >
                  {hourList.map((val, _) => (
                    <option
                      key={val}
                      style={{ height: "30px", cursor: "pointer", background: val === hh ? "#1967D2" : "#fff", color: val === hh ? "#fff" : "#000", border: "1px solid #E2E8F0", textAlign: "center", margin: "2px", paddingTop: "3px" }}
                      value={val}
                    >{val}</option>
                  ))}
                </select>
                <select
                  size={100}
                  style={{ width: "50px", height: "420px", border: "1px solid #E2E8F0", outline: "none", }}
                  ref={minuteSelectRef}
                  value={mm}
                  onChange={(e) => {
                    const value = parseInt(e.target.value);
                    setMm(value);
                  }}
                  onFocus={handleFocus}
                  onBlur={handleBlur}
                >
                  {minutesList.map((val, _) => (
                    <option
                      key={val}
                      style={{ height: "30px", cursor: "pointer", background: val === mm ? "#1967D2" : "#fff", color: val === mm ? "#fff" : "#000", border: "1px solid #E2E8F0", textAlign: "center", margin: "2px", paddingTop: "3px" }}
                      value={val}
                    >{val}</option>
                  ))}
                </select>
              </HStack>
            </PopoverBody>
          </PopoverContent>
        </Portal>
      </Popover>
    </HStack >

  );
}

export default CeasTimePicker;